import React from 'react';
import styled from 'styled-components';

import HomeLogo from '../../common/HomeLogo';
import { NavBar } from '../../common/NavBar';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { styled as muiStyled, useTheme } from '@mui/material/styles';
import Drawer from '@mui/material/Drawer';

const Container = styled.div`
  min-height: 100vh;
  width: 100%;
`;

const Header = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  min-height: 100px;
  grid-template-columns: 1fr 1.2fr 1fr 0.6fr 1.2fr;
  gap: 0;
  grid-auto-flow: row;
  grid-template-areas: 'Logo Navigation Navigation Navigation Navigation';
`;

const HeaderContainer = styled.div`
  min-height: 100px;
`;

const Logo = styled.div`
  grid-area: Logo;
  display: flex;
  align-items: center;
  padding: 0 4rem;
  padding-top: 24px;
  ${({ hasLeftPanel }) => hasLeftPanel && 'background-color: #f4f5f4;'}
  min-width: 320px;
`;

const Navigation = styled.div`
  grid-area: Navigation;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding-left: 5rem;
  padding-right: 4rem;
`;

const Section = styled.div`
  min-height: 50px;
  max-width: 400px;
  width: 100%;
  ${({ type }) =>
    type === 'view' &&
    `
    margin: 0 auto;
    max-width: 640px;
  `}
`;

const Form = styled.div`
  width: 100%;
  display: grid;
  min-height: calc(100vh - 100px);
  position: relative;
  grid-template-columns: 1fr 1.2fr 1fr 0.6fr 1.2fr;
  gap: 0;
  grid-auto-flow: row;
  grid-template-areas: 'MiddlePanel MiddlePanel MiddlePanel MiddlePanel MiddlePanel';
`;

const LeftDrawer = muiStyled(Drawer)(({ zIndex }) => ({
  gridArea: 'LeftPanel',
  width: '100%',
  maxWidth: '400px',
  zIndex: zIndex ?? 9999,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: '100%',
    maxWidth: '400px',
    boxSizing: 'border-box',
    backgroundColor: '#F4F5F4',
  },
}));

const DrawerButton = styled.div`
  position: fixed;
  z-index: 1098;
  background-color: #f4f5f4;
  left: 0;
  top: 0;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DrawerCloseButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 0.3rem;
`;

const MiddlePanel = styled.div`
  grid-area: MiddlePanel;
  padding: 0 4rem;
  padding-bottom: 100px;
`;

const DrawerContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export function PageLayout({
  leftPanel,
  children,
  isOpen,
  hidePanelControls,
  zIndex,
}) {
  const [open, setOpen] = React.useState(false);
  const theme = useTheme();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  return (
    <Container>
      <HeaderContainer>
        <Header>
          <Logo>
            <Section>
              <HomeLogo position="absolute" />
            </Section>
          </Logo>
          <Navigation>
            <Section>
              <NavBar condense={true} />
            </Section>
          </Navigation>
        </Header>
      </HeaderContainer>
      <Form hasLeftPanel={!!leftPanel}>
        {!!leftPanel && (
          <LeftDrawer anchor="left" open={isOpen || open} zIndex={zIndex}>
            <Logo>
              <HomeLogo />
            </Logo>
            <DrawerContainer>
              {leftPanel}
              {!hidePanelControls && (
                <DrawerCloseButton>
                  <IconButton onClick={handleDrawerClose}>
                    {theme.direction === 'ltr' ? (
                      <ChevronLeftIcon />
                    ) : (
                      <ChevronRightIcon />
                    )}
                  </IconButton>
                </DrawerCloseButton>
              )}
            </DrawerContainer>
          </LeftDrawer>
        )}
        <MiddlePanel>{children}</MiddlePanel>
        {!!leftPanel && !hidePanelControls && (
          <DrawerButton onClick={handleDrawerOpen}>
            <ChevronRightIcon />
          </DrawerButton>
        )}
      </Form>
    </Container>
  );
}
